import * as Tone from 'tone';
import type { Chord, VoiceLine, VoicePart } from '@/types';
import type { SuspensionConnector } from '@/types/suspension-connector';
import { audioEngine } from './AudioEngine';

export class PlaybackSystem {
  private scheduledEvents: number[] = [];
  private isPlaying: boolean = false;
  private onPlayheadUpdate?: (beat: number) => void;
  private onChordTrigger?: (chordId: string) => void;
  private onNoteTrigger?: (noteIds: string[]) => void;
  private onSuspensionTrigger?: (connectorId: string, phase: 'suspension' | 'resolution') => void;
  private onPlaybackEnd?: () => void;
  private animationFrameId?: number;
  private totalBeats: number = 16;

  constructor() {
    // Set default tempo
    Tone.Transport.bpm.value = 120;
  }

  /**
   * Convert beat number to Tone.js time format (bars:quarters:sixteenths)
   * Supports fractional beats for sub-beat notes
   */
  private beatToTransportTime(beat: number): string {
    const bars = Math.floor(beat / 4);
    const remainingBeats = beat % 4;
    let quarters = Math.floor(remainingBeats);
    let sixteenths = Math.round((remainingBeats - quarters) * 4);

    // Handle overflow: sixteenths must be 0-3, if it rounds to 4 carry over
    if (sixteenths >= 4) {
      sixteenths = 0;
      quarters += 1;
      // Handle quarters overflow (unlikely but for safety)
      if (quarters >= 4) {
        quarters = 0;
        // Note: bars would need adjustment but beat should never round this high
      }
    }

    return `${bars}:${quarters}:${sixteenths}`;
  }

  /**
   * Schedule a chord progression for playback
   */
  scheduleProgression(chords: Chord[]): void {
    this.clearSchedule();
    // Release any currently playing notes
    audioEngine.stopAll();
    if (chords.length === 0) return;

    const sortedChords = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    const instrument = audioEngine.getCurrentInstrument();

    if (!instrument) {
      console.warn('[Playback] Audio still loading - please wait and try again');
      return;
    }
    // Note: chords.length === 0 check already handled above with early return

    sortedChords.forEach((chord, index) => {
      // Convert startBeat to Tone.js transport time
      const startTime = this.beatToTransportTime(chord.startBeat);

      // Calculate duration: until next chord starts, or use default for last chord
      const nextChord = sortedChords[index + 1];
      const durationBeats = nextChord
        ? nextChord.startBeat - chord.startBeat
        : chord.duration;

      // Schedule chord
      const eventId = Tone.Transport.schedule((time) => {
        const notes = [
          chord.voices.bass,
          chord.voices.tenor,
          chord.voices.alto,
          chord.voices.soprano,
        ].filter(Boolean);

        if (notes.length > 0) {
          // Calculate duration in seconds
          const durationSeconds = (durationBeats / Tone.Transport.bpm.value) * 60;

          // Use triggerAttackRelease for clean attack and release
          instrument.triggerAttackRelease(notes, durationSeconds, time);
        }

        // Notify visual system
        if (this.onChordTrigger) {
          this.onChordTrigger(chord.id);
        }
      }, startTime);

      this.scheduledEvents.push(eventId);
    });
  }

  /**
   * Schedule voice lines for playback (individual melodic notes)
   */
  scheduleVoiceLines(voiceLines: Record<VoicePart, VoiceLine>): void {
    this.clearSchedule();
    audioEngine.stopAll();

    const instrument = audioEngine.getCurrentInstrument();
    if (!instrument) {
      console.warn('[Playback] Audio still loading - please wait and try again');
      return;
    }

    // Collect all notes from all voice parts, grouped by start beat
    const notesByBeat: Map<number, { note: typeof voiceLines.soprano.notes[0]; part: VoicePart }[]> = new Map();

    const parts: VoicePart[] = ['soprano', 'alto', 'tenor', 'bass'];
    parts.forEach(part => {
      const voiceLine = voiceLines[part];

      // Skip if voice line is undefined or missing
      if (!voiceLine) {
        return;
      }

      if (!voiceLine.enabled || voiceLine.muted) return;

      voiceLine.notes.forEach(note => {
        if (note.isRest) return; // Skip rests

        const beat = note.startBeat;
        if (!notesByBeat.has(beat)) {
          notesByBeat.set(beat, []);
        }
        notesByBeat.get(beat)!.push({ note, part });
      });
    });

    // Schedule notes grouped by start beat
    notesByBeat.forEach((notes, beat) => {
      const startTime = this.beatToTransportTime(beat);

      const eventId = Tone.Transport.schedule((time) => {
        // Group notes by duration for efficient scheduling
        const notesByDuration: Map<number, string[]> = new Map();
        const noteIds: string[] = [];

        notes.forEach(({ note }) => {
          noteIds.push(note.id);
          const duration = note.duration;
          if (!notesByDuration.has(duration)) {
            notesByDuration.set(duration, []);
          }
          notesByDuration.get(duration)!.push(note.pitch);
        });

        // Trigger notes grouped by duration
        notesByDuration.forEach((pitches, duration) => {
          const durationSeconds = (duration / Tone.Transport.bpm.value) * 60;
          instrument.triggerAttackRelease(pitches, durationSeconds, time);
        });

        // Notify visual system which notes are playing
        if (this.onNoteTrigger) {
          this.onNoteTrigger(noteIds);
        }
      }, startTime);

      this.scheduledEvents.push(eventId);
    });
  }

  /**
   * Schedule a chord progression with suspension connectors (GRE-113)
   * Suspensions modify timing: the suspended voice holds across the chord change,
   * then resolves after the suspension duration.
   */
  scheduleProgressionWithSuspensions(
    chords: Chord[],
    suspensionConnectors: SuspensionConnector[]
  ): void {
    this.clearSchedule();
    audioEngine.stopAll();
    if (chords.length === 0) return;

    const sortedChords = [...chords].sort((a, b) => a.startBeat - b.startBeat);
    const instrument = audioEngine.getCurrentInstrument();

    if (!instrument) {
      console.warn('[Playback] Audio still loading - please wait and try again');
      return;
    }

    // Create a map of target chord ID -> suspensions affecting it
    const suspensionsByTargetChord = new Map<string, SuspensionConnector[]>();
    suspensionConnectors.forEach((connector) => {
      const existing = suspensionsByTargetChord.get(connector.targetChordId) || [];
      existing.push(connector);
      suspensionsByTargetChord.set(connector.targetChordId, existing);
    });

    // Create a map of source chord ID -> suspensions starting from it
    const suspensionsBySourceChord = new Map<string, SuspensionConnector[]>();
    suspensionConnectors.forEach((connector) => {
      const existing = suspensionsBySourceChord.get(connector.sourceChordId) || [];
      existing.push(connector);
      suspensionsBySourceChord.set(connector.sourceChordId, existing);
    });

    sortedChords.forEach((chord, index) => {
      const startTime = this.beatToTransportTime(chord.startBeat);
      const nextChord = sortedChords[index + 1];
      const durationBeats = nextChord
        ? nextChord.startBeat - chord.startBeat
        : chord.duration;

      // Check if this chord has suspensions targeting it
      const incomingSuspensions = suspensionsByTargetChord.get(chord.id) || [];

      // Get all voices, filtering out suspended ones that will be handled separately
      const suspendedVoices = new Set(incomingSuspensions.map((s) => s.suspendedVoice));

      // Schedule the non-suspended voices of this chord
      const eventId = Tone.Transport.schedule((time) => {
        const notes: string[] = [];

        // Add voices that are NOT suspended
        if (!suspendedVoices.has('bass') && chord.voices.bass) {
          notes.push(chord.voices.bass);
        }
        if (!suspendedVoices.has('tenor') && chord.voices.tenor) {
          notes.push(chord.voices.tenor);
        }
        if (!suspendedVoices.has('alto') && chord.voices.alto) {
          notes.push(chord.voices.alto);
        }
        if (!suspendedVoices.has('soprano') && chord.voices.soprano) {
          notes.push(chord.voices.soprano);
        }

        if (notes.length > 0) {
          const durationSeconds = (durationBeats / Tone.Transport.bpm.value) * 60;
          instrument.triggerAttackRelease(notes, durationSeconds, time);
        }

        // Notify visual system
        if (this.onChordTrigger) {
          this.onChordTrigger(chord.id);
        }
      }, startTime);

      this.scheduledEvents.push(eventId);

      // Schedule resolution notes for incoming suspensions
      incomingSuspensions.forEach((connector) => {
        // The suspension phase: notify UI that suspension is active
        const suspensionEventId = Tone.Transport.schedule(() => {
          // Notify that suspension phase is active
          if (this.onSuspensionTrigger) {
            this.onSuspensionTrigger(connector.id, 'suspension');
          }
        }, startTime);
        this.scheduledEvents.push(suspensionEventId);

        // Schedule the resolution note
        const resolutionBeat = chord.startBeat + connector.suspensionDuration;
        const resolutionTime = this.beatToTransportTime(resolutionBeat);

        // Calculate remaining duration after resolution
        const remainingDurationBeats = durationBeats - connector.suspensionDuration;

        if (remainingDurationBeats > 0) {
          const resolutionEventId = Tone.Transport.schedule((time) => {
            const resolutionDurationSeconds =
              (remainingDurationBeats / Tone.Transport.bpm.value) * 60;

            // Play the resolution note
            instrument.triggerAttackRelease(
              connector.resolutionNote,
              resolutionDurationSeconds,
              time
            );

            // Notify UI of resolution
            if (this.onSuspensionTrigger) {
              this.onSuspensionTrigger(connector.id, 'resolution');
            }
          }, resolutionTime);

          this.scheduledEvents.push(resolutionEventId);
        }
      });

      // For source chords: extend the suspended voice duration
      const outgoingSuspensions = suspensionsBySourceChord.get(chord.id) || [];
      outgoingSuspensions.forEach((connector) => {
        const targetChord = sortedChords.find((c) => c.id === connector.targetChordId);
        if (!targetChord) return;

        // Calculate extended duration: from source chord start to target chord + suspension duration
        const extendedDurationBeats =
          targetChord.startBeat - chord.startBeat + connector.suspensionDuration;

        // Schedule the extended suspension note
        const suspensionNoteEventId = Tone.Transport.schedule((time) => {
          const extendedDurationSeconds =
            (extendedDurationBeats / Tone.Transport.bpm.value) * 60;
          instrument.triggerAttackRelease(
            connector.suspensionNote,
            extendedDurationSeconds,
            time
          );
        }, startTime);

        this.scheduledEvents.push(suspensionNoteEventId);
      });
    });
  }

  /**
   * Start playback
   */
  play(): void {
    if (!audioEngine.getIsInitialized()) {
      console.warn('Audio engine not initialized');
      return;
    }

    this.isPlaying = true;
    Tone.Transport.start();
    this.startPlayheadAnimation();
  }

  /**
   * Pause playback
   */
  pause(): void {
    this.isPlaying = false;
    Tone.Transport.pause();
    this.stopPlayheadAnimation();
  }

  /**
   * Stop and reset to beginning
   */
  stop(): void {
    this.isPlaying = false;
    Tone.Transport.stop();
    Tone.Transport.position = 0;
    audioEngine.stopAll();
    this.stopPlayheadAnimation();

    if (this.onPlayheadUpdate) {
      this.onPlayheadUpdate(0);
    }
  }

  /**
   * Toggle play/pause
   */
  togglePlay(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Seek to a specific beat position (GRE-115)
   * @param beat - The beat number to seek to
   */
  seek(beat: number): void {
    const clampedBeat = Math.max(0, Math.min(beat, this.totalBeats));
    const transportTime = this.beatToTransportTime(clampedBeat);
    Tone.Transport.position = transportTime;

    // Update playhead immediately
    if (this.onPlayheadUpdate) {
      this.onPlayheadUpdate(clampedBeat);
    }
  }

  /**
   * Seek to start (beat 0)
   */
  seekToStart(): void {
    this.seek(0);
  }

  /**
   * Seek to end
   */
  seekToEnd(): void {
    this.seek(this.totalBeats);
  }

  /**
   * Set tempo in BPM
   */
  setTempo(bpm: number): void {
    Tone.Transport.bpm.value = Math.max(60, Math.min(220, bpm));
  }

  /**
   * Play only selected chords (GRE-117)
   * Schedules and plays the given subset of chords
   */
  playSelectedChords(chords: Chord[]): void {
    if (chords.length === 0) return;

    // Stop any current playback
    this.stop();

    // Schedule only the selected chords
    this.scheduleProgression(chords);

    // Start playback
    this.play();
  }

  /**
   * Play a single chord isolated (GRE-117)
   * @param chord - The chord to play
   * @param durationSeconds - Duration in seconds (default 2)
   */
  playChordIsolated(chord: Chord, durationSeconds: number = 2): void {
    const instrument = audioEngine.getCurrentInstrument();
    if (!instrument) {
      console.warn('[Playback] Audio still loading - please wait and try again');
      return;
    }

    // Get all voice notes
    const notes = [
      chord.voices.bass,
      chord.voices.tenor,
      chord.voices.alto,
      chord.voices.soprano,
    ].filter(Boolean);

    if (notes.length > 0) {
      // Play immediately
      instrument.triggerAttackRelease(notes, durationSeconds);

      // Notify visual system
      if (this.onChordTrigger) {
        this.onChordTrigger(chord.id);
      }
    }
  }

  /**
   * Get current tempo
   */
  getTempo(): number {
    return Tone.Transport.bpm.value;
  }

  /**
   * Get current beat position (fractional for smooth animation)
   */
  getCurrentBeat(): number {
    const position = Tone.Transport.position;
    if (typeof position === 'string') {
      // Position format: "bars:quarters:sixteenths"
      const [bars, quarters, sixteenths] = position.split(':').map(Number);
      // Convert to total beats (4 beats per bar)
      return bars * 4 + quarters + (sixteenths / 4);
    }
    return 0;
  }

  /**
   * Check if playing
   */
  getIsPlaying(): boolean {
    return this.isPlaying;
  }

  /**
   * Set playhead update callback
   */
  setPlayheadCallback(callback: (beat: number) => void): void {
    this.onPlayheadUpdate = callback;
  }

  /**
   * Set chord trigger callback
   */
  setChordTriggerCallback(callback: (chordId: string) => void): void {
    this.onChordTrigger = callback;
  }

  /**
   * Set note trigger callback (for voice lines)
   */
  setNoteTriggerCallback(callback: (noteIds: string[]) => void): void {
    this.onNoteTrigger = callback;
  }

  /**
   * Set suspension trigger callback (GRE-113)
   * Called when a suspension phase starts or resolves
   */
  setSuspensionTriggerCallback(
    callback: (connectorId: string, phase: 'suspension' | 'resolution') => void
  ): void {
    this.onSuspensionTrigger = callback;
  }

  /**
   * Set playback end callback
   */
  setPlaybackEndCallback(callback: () => void): void {
    this.onPlaybackEnd = callback;
  }

  /**
   * Set total beats for end detection
   */
  setTotalBeats(beats: number): void {
    this.totalBeats = beats;
  }

  /**
   * Start playhead animation loop
   */
  private startPlayheadAnimation(): void {
    const animate = () => {
      if (!this.isPlaying) return;

      const currentBeat = this.getCurrentBeat();

      // Check if we've reached the end of the timeline
      if (currentBeat >= this.totalBeats) {
        this.stop();
        if (this.onPlaybackEnd) {
          this.onPlaybackEnd();
        }
        return;
      }

      if (this.onPlayheadUpdate) {
        this.onPlayheadUpdate(currentBeat);
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };
    animate();
  }

  /**
   * Stop playhead animation
   */
  private stopPlayheadAnimation(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
  }

  /**
   * Clear all scheduled events
   */
  private clearSchedule(): void {
    this.scheduledEvents.forEach(id => {
      Tone.Transport.clear(id);
    });
    this.scheduledEvents = [];
  }

  /**
   * Dispose resources
   */
  dispose(): void {
    this.stop();
    this.clearSchedule();
  }
}

// Singleton instance
export const playbackSystem = new PlaybackSystem();
